import {startTransition, useCallback, useMemo} from 'react'

import {mergeabilityStatus, type Status} from '../helpers/mergeability-status'
import {useMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import {useMergeabilityLiveUpdates, type Channels} from './use-mergeability-live-updates'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'

interface UseMergeabilityStatusOptions {
  headSha: string
  channels: Channels | null
}

export interface UseMergeabilityStatusResult {
  status: Status | null
  isLoading: boolean
}

export const EMPTY_CHANNELS: Channels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
} as const

/**
 * Custom hook that returns the mergeability status of a pull request, along with a loading state.
 *
 * This hook combines data from the merge box page and the status checks page, and subscribes to live updates via channels.
 *
 * This was created for the ActionabilityBanner component in the pull request overview. Wider adoption of this hook is not recommended at this time.
 *
 * @experimental This hook is experimental and may be subject to change or removal in future releases.
 *
 * @param headSha The head SHA of the pull request, used to fetch status checks data.
 * @param channels An object containing the various channels for live updates. If null, live updates will be disabled.
 * @returns An object containing the mergeability status and a loading state.
 */
export function useMergeabilityStatus({headSha, channels}: UseMergeabilityStatusOptions): UseMergeabilityStatusResult {
  const {mergeMethod} = useMergeMethodContext()
  const {data: mergeBoxData, refetch, isLoading: mergeBoxLoading} = useMergeBoxPageData({mergeMethod})

  const {data: statusChecksData, isLoading: checksLoading} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels: channels ?? EMPTY_CHANNELS})

  const isLoading = mergeBoxLoading || checksLoading

  const status = useMemo(() => {
    if (!mergeBoxData) return null
    return mergeabilityStatus({...mergeBoxData, statusChecksData})
  }, [mergeBoxData, statusChecksData])

  return {status, isLoading}
}
